import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { getInitialFilterValue } from '@pages/Preview/components/BigNumbersFilters/helpers/getInitialFilterValue'
import {
  ADJUST_PREVIEW_REQUIRED_STATUSES,
  PROFILE_CONTROLLER
} from '@pages/Preview/helpers/previewGridValidationActions'
import { initialFormValues } from '@pages/Preview/schema/initialFormValues'
import { usePreview as usePreviewContext } from '@pages/Preview/context'
import { getPagePermissions } from '@hooks/useUser/provider'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { toast } from '@utils/functions/notificationToast'
import { toReferenceDateIso } from '@utils/dates/toReferenceDateIso'
import { useExportPreview } from '../useExportPreview'
import { PageIds } from '@hooks/useLayout/types'
import { useLayout } from '@hooks/useLayout'
import { useUser } from '@hooks/useUser'

import type { DetailedPreviewFiltersFormProps } from '@pages/Preview/components/DetailedPreviewFilters/schema/types'
import type { BigNumbersFiltersFormProps } from '@pages/Preview/components/BigNumbersFilters/Schema/types'
import type {
  BulkReturnPreviewAccountRowProps,
  BulkReturnPreviewCheckResultProps,
  ConsolidatedPreviewGrigFiltersProps,
  SelectConsolidatedPreviewIdProps
} from './types'
import type { AdjustPreviewFormProps } from '@pages/Preview/components/modals/AdjustPreviewModal/form/types'
import type {
  ActionPreviewRequestProps,
  ConsolidatedPreviewDetailsItemProps,
  SaveNewAdjustPreviewRequestProps
} from '@services/api/preview/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'
import type { PreviewFormProps } from '@pages/Preview/schema/types'
import type { AxiosError } from 'axios'

import * as apiPreview from '@services/api/preview'

export const usePreview = () => {
  const navigate = useNavigate()

  const { filiData, userPermissionsData } = useUser.getState()
  const {
    periodSelectedData,
    persistedConsolidatedPreviewFilters,
    setPersistedConsolidatedPreviewFilters
  } = usePreviewContext.getState()
  const { userData } = useUser()

  const {
    exportConsolidatedPreview,
    isOpenConfirmExportPreview,
    setIsOpenConfirmExportPreview,
    exportConsolidatedPreviewLoading
  } = useExportPreview()

  const methods = useForm<PreviewFormProps>({ defaultValues: initialFormValues })

  const bigNumbersFiltersFormValues = useWatch({ control: methods.control, name: 'bigNumbersFilters' })
  const consolidatedPreviewFiltersFormValues = useWatch({
    control: methods.control,
    name: 'consolidatedPreviewFilters'
  })

  const [bigNumbersFiltersForm, setBigNumbersFiltersForm] = useState<BigNumbersFiltersFormProps | null>(null)
  const [consolidatedPreviewFiltersForm, setConsolidatedPreviewFiltersForm] =
    useState<DetailedPreviewFiltersFormProps | null>(null)
  const [selectedConsolidatedPreviewIds, setSelectedConsolidatedPreviewIds] = useState<
    { id: string; weekNumber: number }[]
  >([])
  const [selectedAllConsolidatedPreviewIds, setSelectedAllConsolidatedPreviewIds] = useState<boolean>(false)
  const [consolidatedPreviewGridFilters, setConsolidatedPreviewGridFilters] =
    useState<ConsolidatedPreviewGrigFiltersProps | null>(null)

  const [approveSelectedIsOpen, setApproveSelectedIsOpen] = useState(false)
  const [validateSelectedIsOpen, setValidateSelectedIsOpen] = useState(false)
  const [sendSelectedIsOpen, setSendSelectedIsOpen] = useState(false)
  const [returnPreviousIsOpen, setReturnPreviousIsOpen] = useState(false)
  const [adjustPreviewIsOpen, setAdjustPreviewIsOpen] = useState(false)
  const [returnPreviousSelectedIsOpen, setReturnPreviousSelectedIsOpen] = useState(false)
  const [seeAdjustPreviewIsOpen, setSeeAdjustPreviewIsOpen] = useState(false)
  const [sendPreviewIsOpen, setSendPreviewIsOpen] = useState(false)
  const [validatePreviewIsOpen, setValidatePreviewIsOpen] = useState(false)
  const [approvePreviewIsOpen, setApprovePreviewIsOpen] = useState(false)
  const [justifyingDeviationsIsOpen, setJustifyingDeviationsIsOpen] = useState(false)
  const [blockDeviationIsOpen, setBlockDeviationIsOpen] = useState(false)
  const [blockDeviationMessage, setBlockDeviationMessage] = useState<string | undefined>(undefined)
  const [seeReturnDetailsIsOpen, setSeeReturnDetailsIsOpen] = useState(false)
  const [bonusModalIsOpen, setBonusModalIsOpen] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const [openDeleteAdjustPreviewModal, setOpenDeleteAdjustPreviewModal] = useState(false)
  const [deleteAdjustPreviewLoading, setDeleteAdjustPreviewLoading] = useState(false)
  const [saveAdjustPreviewLoading, setSaveAdjustPreviewLoading] = useState(false)
  const [openAdjustPreviewModal, setOpenAdjustPreviewModal] = useState(false)
  const [isSendPreviewLoading, setIsSendPreviewLoading] = useState(false)
  const [isValidatePreviewLoading, setIsValidatePreviewLoading] = useState(false)
  const [isApprovePreviewLoading, setIsApprovePreviewLoading] = useState(false)
  const [isSendPreviewSelectedLoading, setIsSendPreviewSelectedLoading] = useState(false)
  const [isValidatePreviewSelectedLoading, setIsValidatePreviewSelectedLoading] = useState(false)
  const [isApprovePreviewSelectedLoading, setIsApprovePreviewSelectedLoading] = useState(false)
  const [isCheckingBulkReturnDraftsLoading, setIsCheckingBulkReturnDraftsLoading] = useState(false)
  const [isSavingBulkReturnDraftLoading, setIsSavingBulkReturnDraftLoading] = useState(false)
  const [isSubmittingBulkReturnDraftLoading, setIsSubmittingBulkReturnDraftLoading] = useState(false)
  const [previewGridData, setPreviewGridData] = useState<ConsolidatedPreviewDetailsItemProps[]>([])

  const selectedParentsOnly = useMemo(() => {
    if (!selectedAllConsolidatedPreviewIds && selectedConsolidatedPreviewIds.length === 0) return []
    return !selectedAllConsolidatedPreviewIds
      ? previewGridData.filter((item) =>
          selectedConsolidatedPreviewIds.some(
            (sel) => sel.id === item.profitCenter && sel.weekNumber === item.weekNumber
          )
        )
      : previewGridData
  }, [previewGridData, selectedConsolidatedPreviewIds, selectedAllConsolidatedPreviewIds])

  const hasSelection = selectedConsolidatedPreviewIds.length > 0 || selectedAllConsolidatedPreviewIds

  const bulkActionFlags = useMemo(
    () => ({
      canSubmit: selectedParentsOnly.length > 0 && selectedParentsOnly.every((i) => i.canSubmit),
      canValidate: selectedParentsOnly.length > 0 && selectedParentsOnly.every((i) => i.canValidate),
      canApprove: selectedParentsOnly.length > 0 && selectedParentsOnly.every((i) => i.canApprove),
      canReturn: selectedParentsOnly.length > 0 && selectedParentsOnly.every((i) => i.canReturn)
    }),
    [selectedParentsOnly]
  )

  const getSelectedParentsForApi = (): { branchCode: string; weekNumber: number }[] => {
    return selectedParentsOnly.map((item) => ({
      branchCode: item.profitCenter,
      weekNumber: item.weekNumber
    }))
  }

  const approvePreviewAllSelected = async (items: ActionPreviewRequestProps) => {
    setIsApprovePreviewSelectedLoading(true)

    const allItems = getSelectedParentsForApi()

    try {
      for (const { branchCode, weekNumber } of allItems) {
        await apiPreview.approvePreview({
          branchCode,
          weekNumber,
          referenceDate: items.referenceDate
        })
      }

      toast.success('Prévias selecionadas aprovadas com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage ||
        'Falha ao aprovar as prévias selecionadas!'

      toast.error(errorMessage, (error as AxiosError)?.status)
      return false
    } finally {
      setIsApprovePreviewSelectedLoading(false)
    }
  }

  const approvePreview = async (items: ActionPreviewRequestProps) => {
    setIsApprovePreviewLoading(true)

    try {
      await apiPreview.approvePreview(items)

      toast.success('Prévia aprovada com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage || 'Falha ao aprovar a prévia!'
      toast.error(errorMessage, (error as AxiosError)?.status)
      return false
    } finally {
      setIsApprovePreviewLoading(false)
    }
  }

  const validatePreviewAllSelected = async (items: ActionPreviewRequestProps) => {
    setIsValidatePreviewSelectedLoading(true)

    const allItems = getSelectedParentsForApi()

    try {
      for (const { branchCode, weekNumber } of allItems) {
        await apiPreview.validatePreview({
          branchCode,
          weekNumber,
          referenceDate: items.referenceDate
        })
      }

      await consolidatedPreviewDetails.refetch()

      toast.success('Prévias selecionadas validadas com sucesso!')
      return true
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage ||
        'Falha ao validar as prévias selecionadas!'

      toast.error(errorMessage, (error as AxiosError)?.status)
      return false
    } finally {
      setIsValidatePreviewSelectedLoading(false)
    }
  }

  const validatePreview = async (items: ActionPreviewRequestProps) => {
    setIsValidatePreviewLoading(true)

    try {
      await apiPreview.validatePreview(items)

      toast.success('Prévia validada com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage || 'Falha ao validar a prévia!'
      toast.error(errorMessage, (error as AxiosError)?.status)
      return false
    } finally {
      setIsValidatePreviewLoading(false)
    }
  }

  const sendPreviewSelected = async (items: ActionPreviewRequestProps) => {
    setIsSendPreviewSelectedLoading(true)

    const allItems = getSelectedParentsForApi()

    try {
      for (const { branchCode, weekNumber } of allItems) {
        await apiPreview.sendPreview({
          branchCode,
          weekNumber,
          referenceDate: items.referenceDate,
          sendReasonId: items.sendReasonId
        })
      }

      toast.success('Prévias selecionadas enviadas com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const axiosError = error as AxiosError<{ errorMessage: string }>
      const errorMessage = axiosError?.response?.data?.errorMessage

      if (errorMessage) {
        setBlockDeviationMessage(errorMessage)
        setBlockDeviationIsOpen(true)
      } else {
        toast.error('Falha ao enviar as prévias selecionadas!', axiosError?.status)
      }

      return false
    } finally {
      setIsSendPreviewSelectedLoading(false)
      setSendSelectedIsOpen(false)
    }
  }

  const sendPreview = async (items: ActionPreviewRequestProps) => {
    setIsSendPreviewLoading(true)

    try {
      await apiPreview.sendPreview(items)

      toast.success('Prévia enviada com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const axiosError = error as AxiosError<{ errorMessage: string }>
      const errorMessage = axiosError?.response?.data?.errorMessage

      if (errorMessage) {
        setBlockDeviationMessage(errorMessage)
        setBlockDeviationIsOpen(true)
      } else {
        toast.error('Falha ao enviar a prévia!', axiosError?.status)
      }

      return false
    } finally {
      setIsSendPreviewLoading(false)
      setSendPreviewIsOpen(false)
    }
  }

  const checkBulkReturnPreviewDrafts = async (): Promise<BulkReturnPreviewCheckResultProps[]> => {
    setIsCheckingBulkReturnDraftsLoading(true)

    const allItems = getSelectedParentsForApi()
    const referenceDate = periodSelectedData?.competenceDate || ''

    try {
      return await Promise.all(
        allItems.map(async ({ branchCode, weekNumber }) => {
          try {
            const returnData = await apiPreview.getReturnPreview({ branchCode, weekNumber, referenceDate })
            return { branchCode, weekNumber, itemKey: `${branchCode}-${weekNumber}`, returnData }
          } catch (error) {
            return { branchCode, weekNumber, itemKey: `${branchCode}-${weekNumber}`, returnData: null }
          }
        })
      )
    } finally {
      setIsCheckingBulkReturnDraftsLoading(false)
    }
  }

  const saveDraftReturnPreviewBulk = async (
    accounts: BulkReturnPreviewAccountRowProps[]
  ): Promise<BulkReturnPreviewAccountRowProps[] | null> => {
    setIsSavingBulkReturnDraftLoading(true)

    const allItems = getSelectedParentsForApi()
    const referenceDate = periodSelectedData?.competenceDate || ''

    try {
      const updatedAccounts: BulkReturnPreviewAccountRowProps[] = []

      for (const row of accounts) {
        const accountId = Number(row.account)
        const updatedDraftIds = { ...row.draftIds }

        for (const { branchCode, weekNumber } of allItems) {
          const itemKey = `${branchCode}-${weekNumber}`
          const existingDraftId = updatedDraftIds[itemKey]

          if (existingDraftId) {
            await apiPreview.putReturnDraft(existingDraftId, { accountId, justification: row.returnJustification })
          } else {
            const created = await apiPreview.postReturnDraft({
              branchCode,
              weekNumber,
              referenceDate,
              accountId,
              justification: row.returnJustification
            })
            updatedDraftIds[itemKey] = String(created.id)
          }
        }

        updatedAccounts.push({ ...row, draftIds: updatedDraftIds })
      }

      toast.success('Devolução das prévias selecionadas registrada como rascunho!')

      return updatedAccounts
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage ||
        'Falha ao registrar o rascunho das prévias selecionadas!'

      toast.error(errorMessage, (error as AxiosError)?.status)
      return null
    } finally {
      setIsSavingBulkReturnDraftLoading(false)
    }
  }

  const submitReturnPreviewBulk = async () => {
    setIsSubmittingBulkReturnDraftLoading(true)

    const allItems = getSelectedParentsForApi()
    const referenceDate = periodSelectedData?.competenceDate || ''

    try {
      for (const { branchCode, weekNumber } of allItems) {
        await apiPreview.postReturnSubmit({ branchCode, weekNumber, referenceDate })
      }

      toast.success('Prévias selecionadas devolvidas com sucesso!')

      await consolidatedPreviewDetails.refetch()

      return true
    } catch (error) {
      const errorMessage =
        (error as AxiosError<{ errorMessage: string }>)?.response?.data?.errorMessage ||
        'Falha ao devolver as prévias selecionadas!'

      toast.error(errorMessage, (error as AxiosError)?.status)
      return false
    } finally {
      setIsSubmittingBulkReturnDraftLoading(false)
    }
  }

  const adjustListWeekNumber =
    consolidatedPreviewFiltersForm?.weeks?.length === 1 ?
      Number(consolidatedPreviewFiltersForm.weeks[0])
    : undefined

  const adjustPreviewList = useQuery({
    queryKey: ['adjust-preview-list', filiData?.startPeriod, adjustListWeekNumber],
    queryFn: async () => {
      try {
        return apiPreview.getAdjustPreviewList({
          referenceDate: toReferenceDateIso(filiData?.startPeriod ?? ''),
          ...(adjustListWeekNumber !== undefined && { weeknumber: adjustListWeekNumber })
        })
      } catch (error) {
        toast.error('Falha ao buscar os ajustes da prévia!')
      }
    },
    enabled: !!filiData?.startPeriod && (adjustPreviewIsOpen || seeAdjustPreviewIsOpen)
  })

  const deleteAdjustPreviewById = async (id: string) => {
    setDeleteAdjustPreviewLoading(true)

    try {
      await apiPreview.deleteAdjustPreviewById(id)

      await adjustPreviewList.refetch()
      await consolidatedPreviewDetails.refetch()
      setOpenDeleteAdjustPreviewModal(false)

      toast.success('Ajuste da prévia deletado com sucesso!')
    } catch (error) {
      toast.error('Falha ao deletar o ajuste da prévia!')
    } finally {
      setDeleteAdjustPreviewLoading(false)
    }
  }

  const saveNewAdjustPreview = async (formData: AdjustPreviewFormProps) => {
    const payload: SaveNewAdjustPreviewRequestProps = {
      referenceDate: toReferenceDateIso(filiData?.startPeriod ?? ''),
      period: periodSelectedData?.competencePeriod?.periodCode ?? formData.period,
      weekNumber: formData.weekNumber ?? 0,
      items: formData.accounts!.map((account) => ({
        accountName: account.account,
        adjustmentAmount: unMaskCurrency(account.adjustmentValue) || 0,
        observations: account.observations
      }))
    }

    setSaveAdjustPreviewLoading(true)

    try {
      await apiPreview.saveNewAdjustPreview(payload)

      await adjustPreviewList.refetch()
      await consolidatedPreviewDetails.refetch()
      setOpenAdjustPreviewModal(false)
      setAdjustPreviewIsOpen(false)

      toast.success('Prévia ajustada com sucesso!')
    } catch (error) {
      toast.error('Falha ao salvar o ajuste da prévia!')
    } finally {
      setSaveAdjustPreviewLoading(false)
    }
  }

  const bigNumbersFilters = useQuery({
    queryKey: ['big-numbers-filters', filiData],
    queryFn: async () => {
      try {
        return apiPreview.getBigNumbersFilters({ branchCodes: [filiData?.filiCode!] })
      } catch (error) {
        toast.error('Falha ao buscar os filtros de números gerais!')
      }
    },
    enabled: !!filiData?.filiCode
  })

  // Compartilhado pelas 3 queries que precisam respeitar o filtro do topo (big numbers/gráfico,
  // opções dos filtros da tabela e dados da tabela) — o topo passa a impactar a tela inteira.
  const buildTopFiltersPayload = (filtersForm: BigNumbersFiltersFormProps | null) => ({
    ...(filtersForm?.businessUnit?.length && { businessUnitFilter: filtersForm.businessUnit }),
    ...(filtersForm?.directorate?.length && { boardOfDirectorsFilter: filtersForm.directorate }),
    ...(filtersForm?.portfolio?.length && { portfolioFilter: filtersForm.portfolio }),
    ...(filtersForm?.product?.length && { productFilter: filtersForm.product }),
    ...(filtersForm?.serviceType?.length && { serviceTypeFilter: filtersForm.serviceType }),
    ...(filtersForm?.economicGroup?.length && { economicGroupFilter: filtersForm.economicGroup })
  })

  const buildConsolidatedPreviewPayload = (filtersForm: BigNumbersFiltersFormProps | null) => ({
    referenceDate: filiData?.startPeriod!,
    branchCodes: [filiData?.filiCode!],
    ...buildTopFiltersPayload(filtersForm)
  })

  const consolidatedPreviewData = useQuery({
    queryKey: ['consolidated-preview', filiData, bigNumbersFiltersForm],
    queryFn: async () => {
      try {
        return apiPreview.getConsolidatedPreview(buildConsolidatedPreviewPayload(bigNumbersFiltersForm))
      } catch (error) {
        toast.error('Falha ao buscar os dados da prévia consolidada!')
      }
    },
    enabled: !!filiData?.startPeriod && !!filiData?.filiCode,
    placeholderData: keepPreviousData
  })

  const consolidatedPreviewFilters = useQuery({
    queryKey: ['consolidated-preview-filters', filiData, bigNumbersFiltersForm],
    queryFn: async () => {
      try {
        return apiPreview.getConsolidatedPreviewFilters({
          branchCodeFilter: [filiData?.filiCode!],
          ...buildTopFiltersPayload(bigNumbersFiltersForm)
        })
      } catch (error) {
        toast.error('Falha ao buscar os filtros do detalhamento da prévia consolidada!')
      }
    },
    enabled: !!filiData?.filiCode
  })

  const previewDeadline = useQuery({
    queryKey: ['preview-deadline'],
    queryFn: async () => {
      try {
        return apiPreview.getPreviewDeadline()
      } catch (error) {
        toast.error('Falha ao buscar o prazo de envio da prévia!')
      }
    },
    enabled: !!filiData?.filiCode
  })

  /** Lista fixa de motivos de envio, exigida no modal de Enviar Prévia para perfis ≠ GU. */
  const sendReasons = useQuery({
    queryKey: ['send-reasons'],
    queryFn: async () => {
      try {
        return await apiPreview.getSendReasons()
      } catch (error) {
        toast.error('Falha ao buscar os motivos de envio!')
        return []
      }
    }
  })

  const buildConsolidatedPreviewDetailsPayload = (
    filtersForm: DetailedPreviewFiltersFormProps | null,
    gridFilters: ConsolidatedPreviewGrigFiltersProps | null
  ) => ({
    referenceDate: filiData?.startPeriod!,
    branchCodeFilter: filiData?.filiCode ? [filiData.filiCode] : undefined,
    accountFilter: filtersForm?.account?.length ? filtersForm.account : undefined,
    deviationTypeFilter: filtersForm?.deviationType?.length ? filtersForm.deviationType : undefined,
    statusFilter: filtersForm?.status?.length ? filtersForm.status : undefined,
    weekFilter: filtersForm?.weeks?.length ? filtersForm.weeks : undefined,
    sortBy: gridFilters?.sortBy ?? undefined,
    sortType: gridFilters?.sortType ?? undefined,
    currentPage: gridFilters?.page ?? gridFilters?.currentPage ?? 1,
    itemsPerPage: gridFilters?.itemsPerPage ?? undefined,
    // Grupo Econômico e as demais dimensões agora são exclusivas do filtro do topo — a tabela
    // só refina o que o topo já filtrou (RC01/RC05 da história de integração de filtros).
    ...buildTopFiltersPayload(bigNumbersFiltersForm)
  })

  const consolidatedPreviewDetails = useQuery({
    queryKey: [
      'consolidated-preview-details',
      filiData,
      bigNumbersFiltersForm,
      consolidatedPreviewFiltersForm,
      consolidatedPreviewGridFilters
    ],
    queryFn: async () => {
      try {
        const data = await apiPreview.getConsolidatedPreviewDetails(
          buildConsolidatedPreviewDetailsPayload(consolidatedPreviewFiltersForm, consolidatedPreviewGridFilters)
        )

        setPreviewGridData(data.items)

        return data
      } catch (error) {
        toast.error('Falha ao buscar os dados do detalhamento da prévia consolidada!')
      }
    },
    enabled: !!filiData?.startPeriod && !!filiData?.filiCode
  })

  const onExportConsolidatedPreview = async () => {
    exportConsolidatedPreview(
      buildConsolidatedPreviewDetailsPayload(consolidatedPreviewFiltersFormValues, consolidatedPreviewGridFilters)
    )
  }

  const hasConsolidatedPreviewDetails = useMemo(() => {
    return !!consolidatedPreviewDetails?.data?.items?.length
  }, [consolidatedPreviewDetails?.data?.items])

  const onSeeMorePreview = (item: ConsolidatedPreviewDetailsItemProps) => {
    if (item.account === 'Bonificação') {
      if (filiData?.filiCode === 'ALL' && item.profitCenter) {
        const clickedProfitCenter = userData?.cls?.find((cl) => cl.filiCodigo === item.profitCenter)
        const { fieldsPersistedData, setFieldsPersistedData } = useLayout.getState()

        useUser.getState().setFiliData({
          filiCode: item.profitCenter,
          filiName: clickedProfitCenter?.filiNome || item.profitCenter,
          startPeriod: filiData?.startPeriod || '',
          endPeriod: filiData?.endPeriod || ''
        })

        setFieldsPersistedData({
          competencePeriod: fieldsPersistedData?.competencePeriod || null,
          competenceDate: fieldsPersistedData?.competenceDate || filiData?.startPeriod || null,
          profitCenter: item.profitCenter
        })
      }

      setBonusModalIsOpen(true)
      return
    }

    const routes = {
      1: '/gestao-financeira/faturamento',
      2: '/gestao-financeira/consumo',
      3: '/gestao-financeira/pessoas',
      4: '/gestao-financeira/depreciacao',
      5: '/gestao-financeira/despesas-gerais'
    }

    const route = routes[item.accountId as keyof typeof routes]
    if (!route) return

    // Quando o header está em "Todos", o redirecionamento deve herdar o CL da linha clicada.
    if (filiData?.filiCode === 'ALL' && item.profitCenter) {
      const clickedProfitCenter = userData?.cls?.find((cl) => cl.filiCodigo === item.profitCenter)
      const { fieldsPersistedData, setFieldsPersistedData } = useLayout.getState()

      useUser.getState().setFiliData({
        filiCode: item.profitCenter,
        filiName: clickedProfitCenter?.filiNome || item.profitCenter,
        startPeriod: filiData?.startPeriod || '',
        endPeriod: filiData?.endPeriod || ''
      })

      setFieldsPersistedData({
        competencePeriod: fieldsPersistedData?.competencePeriod || null,
        competenceDate: fieldsPersistedData?.competenceDate || filiData?.startPeriod || null,
        profitCenter: item.profitCenter
      })
    }

    navigate(route)
  }

  const onClearConsolidatedPreviewFilters = () => {
    setConsolidatedPreviewFiltersForm(null)
    setPersistedConsolidatedPreviewFilters(null)
  }

  const onClearBigNumbersFilters = () => {
    setBigNumbersFiltersForm(null)
  }

  const onSelectConsolidatedPreviewId = ({ id, weekNumber, checked }: SelectConsolidatedPreviewIdProps) => {
    if (checked) {
      setSelectedConsolidatedPreviewIds((prev) => [...prev, { id, weekNumber }])
    } else {
      setSelectedConsolidatedPreviewIds((prev) => prev.filter((item) => item.id !== id))
    }
  }

  const onConsolidatedPreviewFilters = () => {
    setBigNumbersFiltersForm(bigNumbersFiltersFormValues)
  }

  const onConsolidatedPreviewDetailsFilters = () => {
    setConsolidatedPreviewFiltersForm(consolidatedPreviewFiltersFormValues)
    setPersistedConsolidatedPreviewFilters(consolidatedPreviewFiltersFormValues)
  }

  const getInitialBigNumbersFilters = () => {
    const bigNumbersFiltersData = bigNumbersFilters.data

    const initialBusinessUnit = getInitialFilterValue(bigNumbersFiltersData?.businessUnit || [])
    const initialDirectorate = getInitialFilterValue(bigNumbersFiltersData?.boardOfDirectors || [])
    const initialPortfolio = getInitialFilterValue(bigNumbersFiltersData?.portfolio || [])
    const initialEconomicGroup = getInitialFilterValue(bigNumbersFiltersData?.economicGroup || [])
    const initialProduct = getInitialFilterValue(bigNumbersFiltersData?.product || [])
    const initialServiceType = getInitialFilterValue(bigNumbersFiltersData?.serviceType || [])

    return {
      businessUnit: initialBusinessUnit ? [initialBusinessUnit] : [],
      directorate: initialDirectorate ? [initialDirectorate] : [],
      portfolio: initialPortfolio ? [initialPortfolio] : [],
      economicGroup: initialEconomicGroup ? [initialEconomicGroup] : [],
      product: initialProduct ? [initialProduct] : [],
      serviceType: initialServiceType ? [initialServiceType] : []
    }
  }

  // Grupo Econômico e Centro de Lucro deixaram de ser filtros da tabela (agora vivem só no
  // topo, ver BigNumbersFilters) — não há mais necessidade de casar o default com o CL do
  // header aqui: as opções do topo já vêm restritas a esse CL (`bigNumbersFilters`, branchCodes:
  // [filiData.filiCode]), então `getInitialFilterValue` já resolve isso sozinho por lá.
  const getInitialConsolidatedPreviewFilters = () => {
    const consolidatedPreviewFiltersData = consolidatedPreviewFilters.data

    const initialAccount = getInitialFilterValue(consolidatedPreviewFiltersData?.account || [])
    const initialDeviationType = getInitialFilterValue(consolidatedPreviewFiltersData?.deviationType || [])
    const initialWeeks = getInitialFilterValue(consolidatedPreviewFiltersData?.weeks || [])

    const profileName = userPermissionsData?.profile?.name
    const profileRequiredStatuses = profileName ? ADJUST_PREVIEW_REQUIRED_STATUSES[profileName] : undefined
    const availableStatusCodes = new Set((consolidatedPreviewFiltersData?.status || []).map((s) => s.code))
    const ignoredInitialStatusCodes = profileName === PROFILE_CONTROLLER ? new Set(['go_validation']) : undefined
    const profileStatusDefaults =
      profileRequiredStatuses?.filter((s) => availableStatusCodes.has(s) && !ignoredInitialStatusCodes?.has(s)) ?? []
    const initialStatus =
      profileStatusDefaults.length > 0
        ? profileStatusDefaults
        : (() => { const v = getInitialFilterValue(consolidatedPreviewFiltersData?.status || []); return v ? [v] : [] })()

    return {
      account: initialAccount ? [initialAccount] : [],
      deviationType: initialDeviationType ? [initialDeviationType] : [],
      status: initialStatus,
      weeks: initialWeeks ? [initialWeeks] : []
    }
  }

  const previousFiliCodeRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const bigData = bigNumbersFilters.data
    const consolidatedData = consolidatedPreviewFilters.data

    if (!filiData || !bigData || !consolidatedData) return

    // `consolidatedPreviewFilters` também refaz a busca quando o filtro do topo é aplicado
    // (sua queryKey inclui `bigNumbersFiltersForm`) — sem essa guarda, clicar em "Filtrar"
    // reseta o formulário inteiro de volta para "Todos" logo em seguida.
    const filiCodeChanged = previousFiliCodeRef.current !== filiData.filiCode
    previousFiliCodeRef.current = filiData.filiCode
    if (!filiCodeChanged) return

    // CL específico no header ainda força recálculo dos defaults da tabela a partir dele
    // (RULES.md), mesmo sem mais Grupo Econômico/Centro de Lucro na tabela.
    const isHeaderCLSpecific =
      !!filiData.filiCode && filiData.filiCode.toLowerCase() !== 'all'
    const preservedConsolidatedFilters =
      isHeaderCLSpecific
        ? getInitialConsolidatedPreviewFilters()
        : (persistedConsolidatedPreviewFilters ?? getInitialConsolidatedPreviewFilters())

    methods.reset({
      bigNumbersFilters: getInitialBigNumbersFilters(),
      consolidatedPreviewFilters: preservedConsolidatedFilters
    })

    if (persistedConsolidatedPreviewFilters) {
      setConsolidatedPreviewFiltersForm(persistedConsolidatedPreviewFilters)
    } else if (!persistedConsolidatedPreviewFilters) {
      const initialFilters = getInitialConsolidatedPreviewFilters()
      const hasProfileStatusDefault = initialFilters.status.some((s) => s !== 'all')
      if (hasProfileStatusDefault) {
        setConsolidatedPreviewFiltersForm(initialFilters)
      }
    }
  }, [filiData, bigNumbersFilters.data, consolidatedPreviewFilters.data, methods])

  useEffect(() => {
    setConsolidatedPreviewFiltersForm(null)
    setPersistedConsolidatedPreviewFilters(null)

    const isHeaderAll =
      !filiData?.filiCode || filiData.filiCode.toLowerCase() === 'all'

    if (!isHeaderAll) return

    methods.reset({
      bigNumbersFilters: methods.getValues('bigNumbersFilters'),
      consolidatedPreviewFilters: {
        account: [],
        deviationType: [],
        status: [],
        weeks: []
      }
    })
  }, [filiData?.filiCode])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.PREVIEW)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return {
    methods,
    bigNumbersFiltersData: bigNumbersFilters.data,
    bigNumbersFiltersLoading: bigNumbersFilters.isLoading,
    consolidatedPreviewDataData: consolidatedPreviewData.data,
    consolidatedPreviewDataLoading: consolidatedPreviewData.isLoading,
    onConsolidatedPreviewFilters,
    consolidatedPreviewFiltersData: consolidatedPreviewFilters.data,
    consolidatedPreviewFiltersLoading: consolidatedPreviewFilters.isLoading,
    previewDeadlineData: previewDeadline.data,
    previewDeadlineLoading: previewDeadline.isLoading,
    sendReasonsData: sendReasons.data,
    sendReasonsLoading: sendReasons.isLoading,
    consolidatedPreviewDetailsData: consolidatedPreviewDetails.data,
    consolidatedPreviewDetailsLoading: consolidatedPreviewDetails.isLoading,
    refetchConsolidatedPreviewDetails: consolidatedPreviewDetails.refetch,
    selectedConsolidatedPreviewIds,
    onSelectConsolidatedPreviewId,
    selectedAllConsolidatedPreviewIds,
    setSelectedAllConsolidatedPreviewIds,
    onConsolidatedPreviewDetailsFilters,
    setConsolidatedPreviewGridFilters,
    getInitialBigNumbersFilters,
    getInitialConsolidatedPreviewFilters,
    consolidatedPreviewFiltersForm,
    consolidatedPreviewGridFilters,
    onExportConsolidatedPreview,
    isOpenConfirmExportPreview,
    setIsOpenConfirmExportPreview,
    exportConsolidatedPreviewLoading,
    onClearConsolidatedPreviewFilters,
    onClearBigNumbersFilters,
    hasConsolidatedPreviewDetails,
    hasSelection,
    bulkActionFlags,
    approveSelectedIsOpen,
    setApproveSelectedIsOpen,
    validateSelectedIsOpen,
    setValidateSelectedIsOpen,
    sendSelectedIsOpen,
    setSendSelectedIsOpen,
    returnPreviousIsOpen,
    setReturnPreviousIsOpen,
    adjustPreviewIsOpen,
    setAdjustPreviewIsOpen,
    returnPreviousSelectedIsOpen,
    setReturnPreviousSelectedIsOpen,
    seeAdjustPreviewIsOpen,
    setSeeAdjustPreviewIsOpen,
    sendPreviewIsOpen,
    setSendPreviewIsOpen,
    validatePreviewIsOpen,
    setValidatePreviewIsOpen,
    approvePreviewIsOpen,
    setApprovePreviewIsOpen,
    justifyingDeviationsIsOpen,
    setJustifyingDeviationsIsOpen,
    blockDeviationIsOpen,
    setBlockDeviationIsOpen,
    blockDeviationMessage,
    seeReturnDetailsIsOpen,
    setSeeReturnDetailsIsOpen,
    bonusModalIsOpen,
    setBonusModalIsOpen,
    permissions,
    setPermissions,
    filiData,
    userPermissionsData,
    periodSelectedData,
    saveNewAdjustPreview,
    adjustPreviewListData: adjustPreviewList.data,
    adjustPreviewListLoading: adjustPreviewList.isFetching,
    openDeleteAdjustPreviewModal,
    setOpenDeleteAdjustPreviewModal,
    deleteAdjustPreviewLoading,
    deleteAdjustPreviewById,
    saveAdjustPreviewLoading,
    openAdjustPreviewModal,
    setOpenAdjustPreviewModal,
    userData: userData?.user || null,
    onSeeMorePreview,
    isSendPreviewLoading,
    sendPreview,
    isValidatePreviewLoading,
    validatePreview,
    isApprovePreviewLoading,
    approvePreview,
    isSendPreviewSelectedLoading,
    sendPreviewSelected,
    isValidatePreviewSelectedLoading,
    validatePreviewAllSelected,
    isApprovePreviewSelectedLoading,
    approvePreviewAllSelected,
    isCheckingBulkReturnDraftsLoading,
    checkBulkReturnPreviewDrafts,
    isSavingBulkReturnDraftLoading,
    saveDraftReturnPreviewBulk,
    isSubmittingBulkReturnDraftLoading,
    submitReturnPreviewBulk,
    selectedParentsOnly
  }
}
